"use client";

/**
 * Hero — single-column centered.
 * Two modes:
 *   Storm active → emergency-first: storm alert, phone, quick actions, THEN persona picker
 *   No storm → explore-first: headline, persona picker, CTA
 * TCI-native: a customer opening this during Hurricane Jolene needs
 * storm status and the phone number in 3 seconds, not 30.
 */
import Link from "next/link";
import { CloudLightning, CircleCheck, Phone, MapPin, TriangleAlert } from "lucide-react";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import { HeroCanvas } from "@/components/landing/HeroCanvas";
import {
  usePersonaStore,
  useActivePersona,
  usePersonaHasHydrated,
  usePhase1Personas,
  PERSONA_AVATAR_COLORS,
  CUSTOMER_TYPE_KEYS,
} from "@/lib/persona";
import { useHurricaneSeason, useHurricaneAnchor, useActiveStorm } from "@petci/shared-ui";
import { getDemoStorm } from "@petci/fixtures";
import { useT, useLocaleTag } from "@/lib/locale";
import { formatEtaLong } from "@/lib/format";

export default function Hero() {
  const t = useT();
  const tag = useLocaleTag();
  const setPersona = usePersonaStore((s) => s.setPersona);
  const activeId = usePersonaStore((s) => s.activeId);
  const persona = useActivePersona();
  const hasHydrated = usePersonaHasHydrated();
  const personas = usePhase1Personas();
  const inSeason = useHurricaneSeason(process.env.NEXT_PUBLIC_FORCE_SEASON);
  const hurricaneNow = useHurricaneAnchor(process.env.NEXT_PUBLIC_HURRICANE_ANCHOR_NOW);
  const storm = useActiveStorm(hurricaneNow, getDemoStorm(hurricaneNow), inSeason);

  const firstName = hasHydrated ? (persona.contactName.split(" ")[0] ?? persona.contactName) : "…";
  const typeKey = CUSTOMER_TYPE_KEYS[persona.customerType] ?? "toolbar.personaTypeResidential";

  return (
    <section className="relative overflow-hidden bg-navy py-16 sm:py-20 lg:py-24 px-4 sm:px-6">
      {/* Gradient mesh — composition + drift live in HeroCanvas (D-C1). The
          section keeps `relative overflow-hidden bg-navy`: that overflow-hidden
          is the structural guard letting the canvas use viewport-relative sizes
          without a page horizontal scroll. */}
      <HeroCanvas />

      <div className="relative max-w-2xl 2xl:max-w-[832px] mx-auto">
        <div className="flex flex-col gap-5 text-center">
            {/* Eyebrow */}
            <span className="overline text-white/70">{t("hero.eyebrow")}</span>

            {/* Headline */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] 2xl:text-[4rem] font-extrabold text-white leading-[1.1]">
              {t("hero.headlinePrimary")}
              <br />
              <span className="text-brand-accent">{t("hero.headlineAccent")}</span>
            </h1>

            {/* Subhead */}
            <p className="text-white/80 text-lg max-w-xl mx-auto leading-relaxed">
              {t("hero.subhead")}
            </p>

            {/* --- Storm-state escalation --- */}
            {storm.active ? (
              <div className="flex flex-col gap-4 self-center w-full max-w-lg">
                {/* Storm alert banner */}
                <div className="bg-status-warning/20 border border-status-warning/40 rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 text-white font-semibold text-sm">
                    <CloudLightning size={18} className="text-status-warning flex-shrink-0" aria-hidden />
                    {/* etaIso + tag: `storm.eta` was baked English and printed an
                        English date inside this translated sentence under es/ht. */}
                    <span>{t("hero.stormEtaBanner", { name: storm.name, eta: formatEtaLong(storm.etaIso, tag) })}</span>
                  </div>
                  <p className="text-white/70 text-xs mt-1 ml-[26px]">
                    {t("hero.stormActive")}
                  </p>
                </div>

                {/* Emergency quick actions */}
                <div className="flex flex-wrap justify-center gap-2">
                  <a
                    href="tel:+16499464313"
                    className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg px-3 py-2 text-sm text-white font-medium transition-colors"
                  >
                    <Phone size={14} aria-hidden />
                    (649) 946-4313
                  </a>
                  <Link
                    href="/report"
                    className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg px-3 py-2 text-sm text-white font-medium transition-colors"
                  >
                    <TriangleAlert size={14} aria-hidden />
                    {t("hero.reportOutage")}
                  </Link>
                  <Link
                    href="/outage/map"
                    className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg px-3 py-2 text-sm text-white font-medium transition-colors"
                  >
                    <MapPin size={14} aria-hidden />
                    {t("hero.checkMap")}
                  </Link>
                  <Link
                    href="/hurricane"
                    className="inline-flex items-center gap-2 bg-status-warning/30 hover:bg-status-warning/40 border border-status-warning/30 rounded-lg px-3 py-2 text-sm text-white font-medium transition-colors"
                  >
                    <CloudLightning size={14} aria-hidden />
                    {t("hero.openHurricanePrep")}
                  </Link>
                </div>
              </div>
            ) : (
              /* --- Non-storm: status pill --- */
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm text-white/90 self-center">
                <CircleCheck size={16} className="text-status-success" aria-hidden />
                <span>{hasHydrated
                  ? t("hero.statusPersona", { island: persona.serviceAddress.island.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) })
                  : t("hero.statusAllClear")
                }</span>
              </div>
            )}

            {/* Persona picker */}
            <div className="w-full max-w-md self-center">
              <p className="text-white/60 text-xs font-medium uppercase tracking-wider mb-2">
                {t("hero.exploreAs")}
              </p>
              <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label={t("hero.exploreAs")}>
                {personas.map((p) => {
                  const isActive = hasHydrated && p.id === activeId;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      onClick={() => setPersona(p.id)}
                      className={[
                        "flex flex-col items-center gap-1 rounded-xl px-2 py-2.5 transition-all duration-fast",
                        isActive
                          ? "bg-white/15 ring-2 ring-brand-accent"
                          : "bg-white/5 hover:bg-white/10 ring-1 ring-white/10",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold",
                          PERSONA_AVATAR_COLORS[p.id] ?? "bg-neutral-300 text-white",
                        ].join(" ")}
                        aria-hidden
                      >
                        {p.contactName.charAt(0)}
                      </span>
                      <span className="text-white text-xs font-medium">{p.contactName.split(" ")[0]}</span>
                      <span className="text-white/60 text-[10px]">{t(CUSTOMER_TYPE_KEYS[p.customerType] ?? "toolbar.personaTypeResidential")}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Persona context + CTA */}
            <p className="text-white/60 text-xs self-center">
              {hasHydrated
                ? t("hero.exploringAs", { name: persona.contactName, type: t(typeKey) })
                : t("hero.exploringGeneric")
              }
            </p>

            {/* Single primary CTA — centered on all breakpoints (F2: the prior
                flex-col→sm:flex-row wrapper was built for two CTAs; at sm+ the axis
                flip left `items-center` aligning vertically, packing the lone CTA left). */}
            <div className="self-center">
              <Link href="/home" className="btn-primary-on-dark text-base px-6 py-3">
                {hasHydrated ? t("hero.enterAs", { name: firstName }) : t("hero.enterGeneric")}
              </Link>
            </div>

            <p className="text-white/50 text-xs self-center">
              {t("hero.trustMicrocopy")}
            </p>

            {/* Language: the availability line hosts a live switcher control
                (L2/D-L2) — the persistent header switcher is the sibling on both
                surfaces. Disclaimer bumped to 12px + honest, undated scope. */}
            <div className="flex flex-wrap items-center justify-center gap-2 self-center">
              <span className="text-white/60 text-xs">{t("hero.langCommitment")}</span>
              <LanguageSwitcher variant="onDark" />
            </div>
            <p className="text-white/60 text-xs self-center max-w-sm">
              {t("hero.langHonestScope")}
            </p>
        </div>
      </div>
    </section>
  );
}
